import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Popover } from "@/components/ui/Popover";
import { fmtMonthYear } from "@/lib/format";
import { BRAND } from "@/lib/tokens";
import type { TimelineRange } from "@/lib/boardView";

interface TimelineCellProps {
  value: TimelineRange | null;
  rowH?: number;
  onPatch?: (value: TimelineRange | null) => void;
}

const PAD = (n: number) => n.toString().padStart(2, "0");
const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function toIso(d: Date): string {
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
}

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function buildGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  const offset = (first.getDay() + 6) % 7;
  const start = addDays(first, -offset);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return cells;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function isBetween(d: Date, a: Date, b: Date): boolean {
  const t = d.getTime();
  return t > Math.min(a.getTime(), b.getTime()) && t < Math.max(a.getTime(), b.getTime());
}

function formatRange(value: TimelineRange): string {
  const s = parseIso(value.start);
  const e = parseIso(value.end);
  if (!s || !e) return "—";
  const sameYear = s.getFullYear() === e.getFullYear();
  const opts: Intl.DateTimeFormatOptions = sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };
  const startStr = s.toLocaleDateString("de-DE", opts);
  const endStr = e.toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

export function TimelineCell({ value, rowH = 44, onPatch }: TimelineCellProps) {
  const editable = typeof onPatch === "function";
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const display = useMemo(() => (value ? formatRange(value) : null), [value]);

  return (
    <>
      <CellWrap
        rowH={rowH}
        onClick={editable ? () => setOpen(true) : undefined}
        interactive={editable}
      >
        <div ref={anchorRef} style={{ width: "100%", display: "flex", alignItems: "center" }}>
          {display ? (
            <span
              title={`${value!.start} – ${value!.end}`}
              style={{
                fontSize: 12.5,
                color: "var(--ink-1)",
                fontFeatureSettings: '"tnum"',
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {display}
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>—</span>
          )}
        </div>
      </CellWrap>
      {editable && open ? (
        <RangePopover
          anchor={anchorRef.current}
          value={value}
          onClose={() => setOpen(false)}
          onCommit={(range) => {
            onPatch?.(range);
            setOpen(false);
          }}
          onClear={() => {
            onPatch?.(null);
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

interface RangePopoverProps {
  anchor: HTMLElement | null;
  value: TimelineRange | null;
  onClose: () => void;
  onCommit: (range: TimelineRange) => void;
  onClear: () => void;
}

function RangePopover({ anchor, value, onClose, onCommit, onClear }: RangePopoverProps) {
  const initialStart = useMemo(() => parseIso(value?.start ?? null), [value?.start]);
  const initialEnd = useMemo(() => parseIso(value?.end ?? null), [value?.end]);
  const todayDate = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const [draftStart, setDraftStart] = useState<Date | null>(initialStart);
  const [draftEnd, setDraftEnd] = useState<Date | null>(initialEnd);
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(initialStart ?? todayDate));
  const gridRef = useRef<HTMLDivElement | null>(null);

  function handleDayClick(d: Date) {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(d);
      setDraftEnd(null);
      return;
    }
    // draftStart set, draftEnd not set — finalize the range.
    if (d.getTime() < draftStart.getTime()) {
      setDraftEnd(draftStart);
      setDraftStart(d);
      onCommit({ start: toIso(d), end: toIso(draftStart) });
    } else {
      setDraftEnd(d);
      onCommit({ start: toIso(draftStart), end: toIso(d) });
    }
  }

  const cells = useMemo(() => buildGrid(viewMonth), [viewMonth]);

  function onGridKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }

  function cellStyle(d: Date): React.CSSProperties {
    const inMonth = sameMonth(d, viewMonth);
    const isStart = draftStart && sameDay(d, draftStart);
    const isEnd = draftEnd && sameDay(d, draftEnd);
    const isToday = sameDay(d, todayDate);
    const inRange = draftStart && draftEnd && isBetween(d, draftStart, draftEnd);
    const base: React.CSSProperties = {
      width: 32,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 0,
      fontSize: 12.5,
      fontFeatureSettings: '"tnum"',
      cursor: "pointer",
      color: inMonth ? "var(--ink-1)" : "var(--ink-4)",
      background: "transparent",
      border: "1px solid transparent",
      boxSizing: "border-box",
      userSelect: "none",
    };
    if (isToday && !isStart && !isEnd) {
      base.border = "1px solid var(--ink-3)";
      base.borderRadius = 6;
    }
    if (inRange) {
      base.background = `${BRAND.blue}22`;
      base.color = "var(--ink-1)";
    }
    if (isStart || isEnd) {
      base.background = BRAND.blue;
      base.color = "#fff";
      base.border = `1px solid ${BRAND.blue}`;
      base.borderRadius = isStart && isEnd ? 6 : isStart ? "6px 0 0 6px" : "0 6px 6px 0";
    }
    return base;
  }

  useEffect(() => {
    requestAnimationFrame(() => gridRef.current?.focus());
  }, []);

  return (
    <Popover anchor={anchor} onClose={onClose} width={280}>
      <div
        style={{ padding: 10, fontFamily: "Inter Variable, Inter, system-ui, sans-serif" }}
        onKeyDown={(e) => {
          if (
            [
              "ArrowUp",
              "ArrowDown",
              "ArrowLeft",
              "ArrowRight",
              "Enter",
              "Escape",
              "PageUp",
              "PageDown",
            ].includes(e.key)
          ) {
            e.stopPropagation();
          }
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            style={chevronStyle}
            aria-label="Vorheriger Monat"
          >
            ◀
          </button>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--ink-1)",
              textTransform: "capitalize",
            }}
          >
            {fmtMonthYear(viewMonth)}
          </div>
          <button
            type="button"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            style={chevronStyle}
            aria-label="Nächster Monat"
          >
            ▶
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0,
            marginBottom: 4,
          }}
        >
          {DOW.map((d) => (
            <div
              key={d}
              style={{
                textAlign: "center",
                fontSize: 10.5,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "4px 0",
              }}
            >
              {d}
            </div>
          ))}
        </div>

        <div
          ref={gridRef}
          tabIndex={0}
          role="grid"
          aria-label="Zeitraum"
          onKeyDown={onGridKey}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0,
            outline: "none",
          }}
        >
          {cells.map((d) => (
            <button
              key={toIso(d)}
              type="button"
              role="gridcell"
              onClick={(e) => {
                e.stopPropagation();
                handleDayClick(d);
              }}
              style={cellStyle(d)}
            >
              {d.getDate()}
            </button>
          ))}
        </div>

        <div
          style={{
            fontSize: 11.5,
            color: "var(--ink-3)",
            marginTop: 8,
            textAlign: "center",
          }}
        >
          {!draftStart
            ? "Klicke auf den Startpunkt"
            : !draftEnd
              ? "Klicke auf den Endpunkt"
              : `${formatRange({ start: toIso(draftStart), end: toIso(draftEnd) })}`}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            paddingTop: 8,
            borderTop: "1px solid var(--hairline)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setDraftStart(null);
              setDraftEnd(null);
              setViewMonth(startOfMonth(todayDate));
            }}
            style={footerBtnStyle}
          >
            Zurücksetzen
          </button>
          {value ? (
            <button
              type="button"
              onClick={onClear}
              style={{ ...footerBtnStyle, color: "var(--danger)" }}
            >
              Löschen
            </button>
          ) : (
            <span />
          )}
        </div>
      </div>
    </Popover>
  );
}

const chevronStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: "1px solid transparent",
  background: "transparent",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--ink-2)",
  fontSize: 11,
  lineHeight: 1,
};

const footerBtnStyle: React.CSSProperties = {
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
};
